import { useState, useEffect, useMemo, useCallback } from 'react';
import { LaunchesService } from '../services/launchesApi';
import type { Launch, LaunchStatus } from '../types';

interface UseLaunchListReturn {
  launches: Launch[];
  filtered: Launch[];
  filter: LaunchStatus | 'all';
  setFilter: (f: LaunchStatus | 'all') => void;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  kpis: LaunchKpis;
}

interface LaunchKpis {
  activeLaunches: number;
  totalAvailable: number;
  totalSold: number;
  totalUnits: number;
}

/**
 * Counts units with 'available' allocation status across all launches.
 */
function countAvailable(launches: Launch[]): number {
  return launches.flatMap((l) => l.units ?? []).filter((u) => u.allocation_status === 'available').length;
}

/**
 * Counts units with 'sold' allocation status across all launches.
 */
function countSold(launches: Launch[]): number {
  return launches.flatMap((l) => l.units ?? []).filter((u) => u.allocation_status === 'sold').length;
}

/**
 * Sums the total number of units across all launches.
 */
function countTotalUnits(launches: Launch[]): number {
  return launches.reduce((sum, l) => sum + (l.units ?? []).length, 0);
}

/**
 * Computes dashboard KPI aggregates from the launch list.
 */
function computeKpis(launches: Launch[]): LaunchKpis {
  const activeLaunches = launches.filter((l) => l.status === 'open').length;
  return {
    activeLaunches,
    totalAvailable: countAvailable(launches),
    totalSold: countSold(launches),
    totalUnits: countTotalUnits(launches),
  };
}

/**
 * Fetches and filters the launch list for the current agency.
 */
export function useLaunchList(agencyId: string): UseLaunchListReturn {
  const [launches, setLaunches] = useState<Launch[]>([]);
  // Opens on "Ativos": a closed or draft launch is not what anyone came
  // here for, and a broker never sees drafts at all.
  const [filter, setFilter] = useState<LaunchStatus | 'all'>('open');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchLaunches = useCallback(() => {
    if (!agencyId) return;
    setIsLoading(true);
    setIsError(false);
    LaunchesService.list(agencyId)
      .then((data) => {
        setLaunches(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [agencyId]);

  useEffect(() => {
    fetchLaunches();
  }, [fetchLaunches]);

  const filtered = useMemo(
    () => (filter === 'all' ? launches : launches.filter((l) => l.status === filter)),
    [launches, filter],
  );

  const kpis = useMemo(() => computeKpis(launches), [launches]);

  return { launches, filtered, filter, setFilter, isLoading, isError, retry: fetchLaunches, kpis };
}
